import React, { useEffect, useMemo } from 'react';
import ThumbnailPicker from './ThumbnailPicker';
import Modal, { ModalProps } from '~/App/Components/Modal';
import { ThumbnailArgs } from '~/App/Components/Record/Stores/Recorder/Thumbnail';
import {
  EditableThumbnailResourcePage,
  EditableThumbnailResourceType,
} from '~/types/models/thumbnail';
import useAnalytics from '~/react-hooks/use-analytics';
import {
  VIDEO_EDIT_THUMBNAIL,
  VIDEO_EDIT_THUMBNAIL_SAVE,
} from '~/generated/AnalyticsTypes';

export interface ThumbnailPickerModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (thumbnail: ThumbnailArgs) => Promise<void>;
  saving: boolean;
  videoUrl: string;
  resourcePage?: EditableThumbnailResourcePage;
  resourceType?: EditableThumbnailResourceType;
}

export default function ThumbnailPickerModal({
  open,
  onClose,
  onSave,
  saving,
  videoUrl,
  resourcePage,
  resourceType,
}: ThumbnailPickerModalProps) {
  const analytics = useAnalytics();

  const modalProps = useMemo<ModalProps>(() => {
    return {
      isOpen: open,
      onRequestClose: onClose,
      styles: {
        overlay: ({ base }) => ({
          ...base,
          display: 'flex',
        }),
        content: ({ base }) => ({
          ...base,
          width: 955,
          maxWidth: 955,
          padding: 'unset',
          margin: 'auto',
        }),
        panel: ({ base }) => ({
          ...base,
          padding: '40px',
          borderRadius: 5,
          minHeight: 679,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }),
      },
      hideActions: true,
      showCloseButton: true,
    };
  }, [open, onClose]);

  const track = (event: string) => {
    if (resourcePage && resourceType) {
      analytics.track(event, {
        context: resourcePage,
        asset_type: resourceType,
      });
    }
  };

  useEffect(() => {
    if (open) {
      track(VIDEO_EDIT_THUMBNAIL);
    }
  }, [open, resourcePage, resourceType]);

  return (
    <Modal {...modalProps}>
      <div className='flex flex-col justify-center pt-4 h-full flex-1'>
        <ThumbnailPicker
          videoUrl={videoUrl}
          onCancel={onClose}
          onSave={async (thumb) => {
            await onSave(thumb);
            track(VIDEO_EDIT_THUMBNAIL_SAVE);
            onClose();
          }}
          saving={saving}
        />
      </div>
    </Modal>
  );
}
